import { inject, injectable } from 'inversify';
import AuthService from 'src/features/auth/data/service/authService';
import { GetCurrentUserResult } from 'src/features/auth/domain/useCases/types';
import {
  Address,
  AddressDetails,
  AddressGeolocation,
  UserDetails,
  UserProfile,
} from 'src/types/common';
import { getStorageSession } from 'src/utils/localStorage/session';
import deepEqual from 'src/utils/objects/deepEqual';

@injectable()
class GetCurrentUserUseCase implements GetCurrentUserUseCase {
  @inject(AuthService) private authService!: AuthService;

  async execute(): Promise<GetCurrentUserResult> {
    const session = getStorageSession();

    if (!session || !session.accessToken || !session.user?.pk) {
      throw new Error('No hay sesión activa o faltan datos necesarios');
    }

    const { accessToken, user: localUser } = session;

    const response = await this.authService.getUserById({
      user_pk: localUser.pk,
      token: accessToken,
    });

    const serviceUser = response.data;

    const isUpdated = !this.areUserDetailsEqual(localUser, serviceUser);

    return {
      ...serviceUser,
      updated: isUpdated,
    };
  }

  public areUserDetailsEqual(user1: UserDetails, user2: UserDetails): boolean {
    const keysToCompare: (keyof UserDetails)[] = [
      'pk',
      'uuid',
      'email',
      'first_name',
      'last_name',
      'phone_number',
      'phone_verified',
      'type',
      'is_completed',
      'id_verified',
      'email_verified',
    ];

    const areSimpleKeysEqual = keysToCompare.every(
      (key) => user1[key] === user2[key]
    );

    const areAddressesEqual = this.areAddressesEqual(
      user1.main_address,
      user2.main_address
    );

    const areProfilesEqual = this.areProfilesEqual(
      user1.profile,
      user2.profile
    );

    return areSimpleKeysEqual && areAddressesEqual && areProfilesEqual;
  }

  private areAddressesEqual(
    address1: Address | null,
    address2: Address | null
  ): boolean {
    if (!address1 || !address2) return address1 === address2;

    const keysToCompare: (keyof Address)[] = ['pk'];

    const areSimpleKeysEqual = keysToCompare.every(
      (key) => address1[key] === address2[key]
    );

    const isStoreDataEqual = deepEqual(
      address1.store_data,
      address2.store_data
    );

    const areAddressDetailsEqual = this.areAddressDetailsEqual(
      address1.address,
      address2.address
    );

    return areSimpleKeysEqual && areAddressDetailsEqual && isStoreDataEqual;
  }

  public areAddressDetailsEqual(
    details1: AddressDetails,
    details2: AddressDetails
  ): boolean {
    const keysToCompare: (keyof AddressDetails)[] = [
      'street_name',
      'street_number',
      'extra_info',
      'city',
      'state',
      'zip_code',
      'neighborhood',
      'formatted_address',
      'interior_number',
    ];

    const areSimpleKeysEqual = keysToCompare.every(
      (key) => details1[key] === details2[key]
    );

    const areGeolocationsEqual = this.areGeolocationsEqual(
      details1.geolocation,
      details2.geolocation
    );

    return areSimpleKeysEqual && areGeolocationsEqual;
  }

  public areGeolocationsEqual(
    geo1: AddressGeolocation | undefined,
    geo2: AddressGeolocation | undefined
  ): boolean {
    if (!geo1 || !geo2) return geo1 === geo2;

    const isTypeEqual = geo1.type === geo2.type;
    const areCoordinatesEqual =
      geo1.coordinates[0] === geo2.coordinates[0] &&
      geo1.coordinates[1] === geo2.coordinates[1];

    return isTypeEqual && areCoordinatesEqual;
  }

  public areProfilesEqual(
    profile1: UserProfile | null,
    profile2: UserProfile | null
  ): boolean {
    if (!profile1 || !profile2) return profile1 === profile2;

    const keysToCompare: (keyof UserProfile)[] = [
      'birth_date',
      'document_type',
      'document_number',
    ];

    return keysToCompare.every((key) => profile1[key] === profile2[key]);
  }
}

export default GetCurrentUserUseCase;
